import React, { useEffect, useState } from "react";
import { Form, Input, DatePicker, Select, Radio } from "antd";
import ModalComponent from "@/components/modal";
import ButtonComponent from "@/components/button/Button";
import { setModalOpen } from "@/reducers/commonSlice";
import { useDispatch } from "react-redux";
import dayjs from "dayjs";
import { getGuideConversations } from "@/services/guides/getGuideConversations";

const { TextArea } = Input;
const { Option } = Select;

const AddNewTaskForm = ({
  isOpen,
  title = "Add New Task",
  initialValues = {},
  onSubmit = () => {},
  onCancel = () => {},
  btnLabel = "Submit Task",
  isOwnTask = false,
}) => {
  const [form] = Form.useForm();
  const [taskType, setTaskType] = useState(isOwnTask ? "own" : "student");
  const [products, setProducts] = useState([]); // State for product list
  const [studentsData, setStudentsData] = useState({}); // State to hold API data
  const [loading, setLoading] = useState(false); // Loading state for fetch
  const dispatch = useDispatch();

  // Fetch API data
  useEffect(() => {
    const fetchStudentsData = async () => {
      setLoading(true);
      try {
        const response = await getGuideConversations(); // Replace with your API URL
        setStudentsData(response);
      } catch (error) {
        console.error("Error fetching students data:", error);
      } finally {
        setLoading(false);
      }
    };

    if (isOpen && !isOwnTask) {
      fetchStudentsData();
      form.setFieldsValue(initialValues);
    } else {
      form.resetFields();
      setProducts([]);
    }
  }, [isOpen]);

  const handleStudentChange = (studentId) => {
    const selectedStudent = Object.values(studentsData).find((student) => student.id === studentId);
    const productList = selectedStudent?.purchases || [];
    setProducts(productList);
    form.setFieldsValue({ product: undefined }); // Reset product selection
  };

  const handleFinish = (values) => {
    console.log("Form Submitted: ", values);
    onSubmit(values, taskType === "own");
  };

  const handleCancel = () => {
    form.resetFields();
    setTaskType(isOwnTask ? "own" : "student");
    dispatch(setModalOpen(false));
  };

  const handleTaskTypeChange = (e) => {
    setTaskType(e.target.value);
    setProducts([]);
    form.setFieldsValue({ students: undefined, product: undefined });
  };

  return (
    <ModalComponent isOpen={isOpen} onCancel={handleCancel} width={500}>
      <div className="form-heading title-h5-1">{title}</div>
      <div className="task-form">
        <Form form={form} name="addNewTask" layout="vertical" size="small" onFinish={handleFinish}>
          {/* Task Type */}
          {!isOwnTask && (
            <Form.Item label="Task Type" name="taskType">
              <Radio.Group onChange={handleTaskTypeChange} defaultValue="student" value={taskType}>
                <Radio value="student">Create Student Task</Radio>
                <Radio value="own">Create Own Task</Radio>
              </Radio.Group>
            </Form.Item>
          )}

          {/* Title */}
          <Form.Item
            label="Title"
            name="title"
            rules={[{ required: true, message: "Please enter the task name!" }]}
          >
            <Input placeholder="Task Name" />
          </Form.Item>

          {/* Deadline */}
          <Form.Item
            label="Deadline"
            name="deadline"
            rules={[
              { required: true, message: "Please select a deadline!" },
              () => ({
                validator(_, value) {
                  if (!value || value.isAfter()) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error("Deadline cannot be in the past!"));
                },
              }),
            ]}
          >
            <DatePicker
              placeholder="Select your deadline date"
              disabledDate={(current) => current && current < dayjs().startOf("day")}
            />
          </Form.Item>

          {/* Description */}
          <Form.Item
            label="Description"
            name="description"
            rules={[{ required: true, message: "Please enter a description!" }]}
          >
            <TextArea
              placeholder="Enter task description..."
              autoSize={{ minRows: 3, maxRows: 5 }}
            />
          </Form.Item>

          {/* Conditional Fields */}
          {taskType === "student" && (
            <>
              {/* Students */}
              <Form.Item
                label="Students"
                name="students"
                rules={[{ required: true, message: "Please select students!" }]}
              >
                <Select
                  virtual={false}
                  placeholder={loading ? "Loading students..." : "Select Students"}
                  onChange={handleStudentChange}
                  loading={loading}
                >
                  {Object.values(studentsData).map((student) => (
                    <Option key={student.id} value={student.id}>
                      {student.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              {/* Products */}
              <Form.Item
                label="Product"
                name="product"
                rules={[{ required: true, message: "Please select product!" }]}
              >
                <Select virtual={false} placeholder="Select Product">
                  {products.map((product) => (
                    <Option key={product.productId} value={product.productId}>
                      {product.productName}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </>
          )}

          {/* Submit Button */}
          <Form.Item>
            <div className="button">
              <ButtonComponent label={btnLabel} varient="primary" htmlType="submit" />
            </div>
          </Form.Item>
        </Form>
      </div>
    </ModalComponent>
  );
};

export default AddNewTaskForm;
